import { type IDataObject, type IDataObjectIdentity } from '@inpart-io/devtools-iap';
import { RECEIVE_DATA_OBJECTS, REMOVE_DATA_OBJECTS, STORE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import { type UploadManager } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { SUPPORT_FOLDER } from '@/constants/attachments.constants';

import { RECEIVE_CHECK_STATUS } from './mutations';
import { type ICheckStatusState } from './types';

export const CHECK_FILE_IS_REMOVABLE = 'checkFileIsRemovable';
export const DELETE_FILE = 'deleteFile';
export const CASCADE_DELETE_FILE = 'cascadeDeleteFile';
export const UPLOAD_FILE_TO_OBJECT = 'uploadFileToObject';
export const MOVE_TO_FOLDER = 'moveToFolder';

export function getActions<T extends IDataObject>(manager: UploadManager<T>): ActionTree<ICheckStatusState<T>, unknown> {
    return {
        async [UPLOAD_FILE_TO_OBJECT](
            context,
            {
                objectId,
                objectType,
                file,
                folderId,
                newFileName,
                links,
                properties
            }: {
                objectId?: number;
                objectType?: string;
                file?: File;
                folderId?: number;
                newFileName?: string;
                links?: IDataObjectIdentity[];
                properties?: string;
            }
        ): Promise<number> {
            const createRootFolder = SUPPORT_FOLDER.includes(objectType) && (folderId === undefined || folderId === null);

            const result: IDataObject = (await manager.uploadFileOnContainer(objectId, objectType, file, folderId, createRootFolder, newFileName, links, {
                partial: true,
                properties
            })) as IDataObject;
            context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: [result] });
            return result.dobjId;
        },
        async [CHECK_FILE_IS_REMOVABLE](context, { fileId }: { fileId?: number }) {
            const result: unknown = await manager.checkCascadeDeletion(fileId);
            context.commit(RECEIVE_CHECK_STATUS, { dobjId: fileId, status: result });
        },
        async [DELETE_FILE](context, { fileId, fieldName = 'preview' }: { fileId?: number; fieldName?: string }): Promise<number> {
            await manager.delete(fileId);
            manager.removeBinaryFieldBlobUrl(fileId, fieldName);
            context.commit(REMOVE_DATA_OBJECTS, { dobjId: fileId });
            return fileId;
        },
        async [CASCADE_DELETE_FILE](context, { fileId, fieldName = 'preview' }: { fileId?: number; fieldName?: string }): Promise<number> {
            await manager.cascadeDelete(fileId);
            manager.removeBinaryFieldBlobUrl(fileId, fieldName);
            context.commit(REMOVE_DATA_OBJECTS, { dobjId: fileId });
            return fileId;
        },
        async [MOVE_TO_FOLDER](
            context,
            { dobjId, destinationId, properties }: { dobjId: number; destinationId: number; properties?: string }
        ): Promise<number> {
            const result = await manager.moveFileToFolder(dobjId, destinationId, { properties });
            void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
            return result.dobjId;
        }
    };
}
